/**
 * Credit gate rule card per scope §6.6.2. Atlas Day 3a P1.
 *
 * Surface: "ISG extends credit to N of M distributors. Payment terms range
 * net-X to net-Y. Credit posture concentrated in K distributors."
 * Substrate-driven from distributors with credit_limit > 0 + payment_terms
 * range across active distributors.
 *
 * D-1 lock 2026-05-27: credit_ledger schema actual columns (event_type +
 * notes + balance_after + po_id etc.) surface in inspect L8 propagation
 * cascade; not in surface line.
 *
 * Click handler navigates to inspect view per Atlas Day 2.5 P1 URL routing.
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import type { CreditGateSubstrate } from '../../../lib/atlas/transformationRowSubstrate';

export interface CreditGateCardProps {
  substrate: CreditGateSubstrate;
}

export default function CreditGateCard({ substrate }: CreditGateCardProps): JSX.Element {
  const navigate = useNavigate();
  const { extendsCreditCount, totalActiveDistributors, paymentTermsRange } = substrate;
  const termsClause = paymentTermsRange
    ? paymentTermsRange.min === paymentTermsRange.max
      ? `Payment terms net-${paymentTermsRange.min}.`
      : `Payment terms range net-${paymentTermsRange.min} to net-${paymentTermsRange.max}.`
    : 'Payment terms not yet configured.';
  return (
    <button
      type="button"
      data-sub-region-id="credit-gate"
      onClick={() => navigate('/admin/engine-atlas/inspect/credit-gate')}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <CreditCard size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ink-bright">Credit gate</h3>
          <p className="text-xs text-ink-secondary mt-1">
            ISG extends credit to {extendsCreditCount} of {totalActiveDistributors}{' '}
            {totalActiveDistributors === 1 ? 'distributor' : 'distributors'}. {termsClause}{' '}
            Credit posture concentrated in {extendsCreditCount}{' '}
            {extendsCreditCount === 1 ? 'distributor' : 'distributors'}.
          </p>
        </div>
      </div>
    </button>
  );
}
